import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Slider } from "@mui/material";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import { Typography, Select, Input, Button } from "design_system/src";
import { TVehicle } from "models/inventory/interfaces/vehicle.interface";
import { TSku } from "models/inventory/interfaces/sku.interface";
import { ILoanOffer, LoanOfferResponse } from "models/loanOffer/interfaces/loanOffer.interface";
import { LoanOfferRejectionReason } from "models/loanOffer/enums/loanOffer.enums";
import {
  calculateLoanOffer,
  calculateCreditLineOffer,
} from "models/loanOffer/controllers/loanOffer.controller";
import {
  setApplicationLoanTime,
  setApplicationDownPayment,
} from "store/actions/application";
import { useFinancialForm } from "hooks/useFinancialForm";
import {
  ICreditLineFinancialForm,
  ILoanFinancialForm,
  isCreditLineFinancialForm,
} from "models/financialForm/interfaces/financialForm.interface";
import { priceFormatFinancial } from "helpers/prices";
import { ReactComponent as Lock } from "design_system/src/static/icons/lock-circle.svg";
import { ReactComponent as Warning } from "design_system/src/static/icons/street-warning.svg";
import { IPreOfferApproved } from "models/loanOffer/interfaces/preOffer.interface";
import {
  downPaymentMessage,
  isValidRequest,
} from "models/loanOffer/helpers/loanCalculator.helpers";
import { getLatestPreOffer, isApprovedPreOffer } from "models/loanOffer/helpers/preOffer.helpers";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "@material-ui/icons";

const SLIDER_STEP = 50;

interface LoanCalculatorProps {
  vehicle?: TVehicle;
  sku?: TSku;
  price?: number;
  showVehicleDetail?: boolean;
  readOnly?: boolean;
  onContinueBtn?: () => void;
  btnDisabled?: boolean;
}

const sortOffersByDuration = (offers: ILoanOffer[]): ILoanOffer[] =>
  [...offers].sort((a, b) => a.duration - b.duration);

const resolveSelectedDuration = (
  offers: ILoanOffer[],
  options: { preserveSavedDuration?: boolean; savedDuration?: number }
): number | undefined => {
  const sorted = sortOffersByDuration(offers);
  const fallback = sorted[0]?.duration;

  if (options.preserveSavedDuration && options.savedDuration) {
    const savedOffer = sorted.find((o) => o.duration === options.savedDuration);
    if (savedOffer) return options.savedDuration;
  }

  return fallback;
};

const ReachChip: React.FC<{ reaches: boolean }> = ({ reaches }) => (
  <span
    className={`br_sm p_x_sm p_y_xxs ${reaches ? "bg_green_100 text_green_300" : "bg_red_100 text_red_300"
      }`}
    style={{ display: "inline-block" }}
  >
    <Typography scale="xsmall" weight="600" component="span">
      {reaches ? "Te alcanza" : "No te alcanza"}
    </Typography>
  </span>
);

const PlanRow: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
  error?: boolean;
  loading?: boolean;
  sub?: boolean;
}> = ({ label, value, highlight, error, loading, sub }) => {
  // eslint-disable-next-line no-nested-ternary
  const color = error ? "red_300" : sub ? "neutral_700" : "neutral_800";
  const scale = sub ? "xsmall" : "small";
  return (
    <div className={`display_flex flex_justify_between flex_align_center p_y_xxs ${sub ? "p_l_md" : ""}`}>
      <Typography scale={scale} weight={highlight ? "600" : "400"} textColor={color}>
        {sub ? `↳ ${label}` : label}
      </Typography>
      {loading && <div>...</div>}
      {!loading && (
        <Typography scale={scale} weight={highlight ? "600" : "400"} textColor={color}>
          {value}
        </Typography>
      )}
    </div>
  );
};

const LoanCalculator: React.FC<LoanCalculatorProps> = ({
  vehicle,
  sku,
  price,
  showVehicleDetail = false,
  readOnly = false,
  onContinueBtn,
  btnDisabled = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { financialForm } = useFinancialForm();
  const isCreditLine = isCreditLineFinancialForm(financialForm);
  const loanFinancialForm = !isCreditLine
    ? (financialForm as ILoanFinancialForm | null)
    : null;
  const creditLineFinancialForm = isCreditLine
    ? (financialForm as ICreditLineFinancialForm | null)
    : null;

  const [preOffer, setPreOffer] = useState<IPreOfferApproved>();
  const [loading, setLoading] = useState(true);
  const [offerResult, setOfferResult] = useState<LoanOfferResponse | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>();
  const [downPayment, setDownPayment] = useState<number>(0);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [downPaymentInput, setDownPaymentInput] = useState<string>("");
  const selectedDurationRef = useRef(selectedDuration);

  const vehiclePrice = vehicle?.marketPrice ?? price ?? 0;
  const subjectImage = vehicle?.images?.[0] ?? sku?.images?.[0];
  const subjectTitle = vehicle
    ? `${vehicle.brand.name} ${vehicle.model.name}`
    : `${sku?.brandName ?? ""} ${sku?.modelName ?? ""}`;
  const subjectCc = vehicle?.cylindersCapacity ?? sku?.cc;
  const subjectYear = vehicle?.year ?? sku?.year;

  useEffect(() => {
    selectedDurationRef.current = selectedDuration;
  }, [selectedDuration]);

  useEffect(() => {
    if (!loanFinancialForm) {
      setPreOffer(undefined);
      return;
    }
    const preOfferObj = getLatestPreOffer(loanFinancialForm.loanApplication?.preOffers);
    setPreOffer(
      preOfferObj && isApprovedPreOffer(preOfferObj)
        ? (preOfferObj as IPreOfferApproved)
        : undefined
    );
  }, [loanFinancialForm]);

  const fetchOffers = useCallback(
    async (
      ticketPrice: number,
      downPaymentAmount: number,
      preserveSavedDuration = false,
      isRetry = false
    ) => {
      if (!financialForm || (!isCreditLine && !preOffer)) {
        setLoading(false);
        return;
      }
      const result = isCreditLine
        ? await calculateCreditLineOffer(financialForm._id, {
          ticketPrice,
          downPaymentAmount,
        })
        : await calculateLoanOffer(financialForm._id, {
          ticketPrice,
          downPaymentAmount,
        });

      if (!result) {
        setLoading(false);
        return;
      }

      if (
        !isRetry &&
        result.status === "rejected" &&
        result.reason === LoanOfferRejectionReason.InvalidParams &&
        result.minDownPaymentAmount !== undefined
      ) {
        const minDownPayment = Math.round(result.minDownPaymentAmount);
        setDownPayment(minDownPayment);
        setSliderValue(minDownPayment);
        setDownPaymentInput(minDownPayment > 0 ? String(minDownPayment) : "");
        dispatch(setApplicationDownPayment(minDownPayment));
        await fetchOffers(ticketPrice, minDownPayment, preserveSavedDuration, true);
        return;
      }

      setOfferResult(result);

      const savedDuration = preserveSavedDuration
        ? selectedDurationRef.current ??
          (isCreditLine
            ? creditLineFinancialForm?.loanDuration
            : loanFinancialForm?.loanApplication?.loanDuration)
        : undefined;

      const resolvedDuration = resolveSelectedDuration(result.offers, {
        preserveSavedDuration,
        savedDuration,
      });

      if (resolvedDuration) {
        setSelectedDuration(resolvedDuration);
        dispatch(setApplicationLoanTime(resolvedDuration));
      }
    },
    [financialForm, isCreditLine, preOffer, loanFinancialForm, creditLineFinancialForm]
  );

  const debouncedFetch = useMemo(
    () => debounce((ticketPrice: number, downPaymentAmount: number) => {
      fetchOffers(ticketPrice, downPaymentAmount, true);
    }, 300),
    [fetchOffers]
  );

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  useEffect(() => {
    if (offerResult !== null) setLoading(false);
  }, [offerResult]);

  useEffect(() => {
    if (!financialForm || (!isCreditLine && !preOffer)) return;

    const initialDownPayment = Math.round(
      (isCreditLine
        ? creditLineFinancialForm?.downPayment
        : loanFinancialForm?.loanApplication?.downPayment) ?? 0
    );
    setDownPayment(initialDownPayment);
    setSliderValue(initialDownPayment);
    setDownPaymentInput(initialDownPayment > 0 ? String(initialDownPayment) : "");
    dispatch(setApplicationDownPayment(initialDownPayment));
    fetchOffers(vehiclePrice, initialDownPayment, true);
  }, [financialForm?._id, isCreditLine, preOffer, vehiclePrice, fetchOffers]);

  const downPaymentBounds = useMemo(() => {
    if (
      !offerResult ||
      offerResult.minDownPaymentAmount === undefined ||
      offerResult.maxDownPaymentAmount === undefined
    ) {
      return undefined;
    }
    return {
      min: Math.round(offerResult.minDownPaymentAmount),
      max: Math.round(offerResult.maxDownPaymentAmount),
    };
  }, [offerResult]);

  useEffect(() => {
    if (!downPaymentBounds) return;

    const clamped = Math.min(
      downPaymentBounds.max,
      Math.max(downPaymentBounds.min, downPayment)
    );

    if (clamped !== downPayment) {
      setDownPayment(clamped);
      setSliderValue(clamped);
      setDownPaymentInput(clamped > 0 ? String(clamped) : "");
      dispatch(setApplicationDownPayment(clamped));
      fetchOffers(vehiclePrice, clamped, true);
    }
  }, [downPaymentBounds]);

  const allOffers = useMemo<ILoanOffer[]>(() => {
    if (!offerResult?.offers.length) return [];
    return sortOffersByDuration(offerResult.offers);
  }, [offerResult]);

  const selectedOffer = useMemo<ILoanOffer | undefined>(
    () => allOffers.find((o) => o.duration === selectedDuration),
    [allOffers, selectedDuration]
  );

  const reaches = useMemo<boolean>(
    () =>
      offerResult !== null &&
      offerResult.offers.length > 0 &&
      isValidRequest(offerResult, downPayment),
    [offerResult, downPayment, loading]
  );

  const downPaymentFeedback = useMemo(() => {
    if (reaches || !offerResult || offerResult.offers.length === 0) return null;
    return downPaymentMessage(offerResult, downPayment);
  }, [reaches, offerResult, downPayment]);

  const commitDownPaymentChange = useCallback(
    (rawValue: number) => {
      if (readOnly || (!isCreditLine && !preOffer) || !downPaymentBounds) return;

      const value = Math.min(
        downPaymentBounds.max,
        Math.max(downPaymentBounds.min, Math.round(rawValue))
      );

      setDownPayment(value);
      setSliderValue(value);
      setDownPaymentInput(value > 0 ? String(value) : "");
      setLoading(true);
      debouncedFetch(vehiclePrice, value);
      dispatch(setApplicationDownPayment(value));
    },
    [
      readOnly,
      isCreditLine,
      preOffer,
      downPaymentBounds,
      vehiclePrice,
      financialForm,
      selectedDuration,
      debouncedFetch,
      dispatch,
      vehicle,
      sku,
    ]
  );

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    if (readOnly) return;
    const value = newValue as number;
    setSliderValue(value);
    setDownPaymentInput(String(value));
  };

  const handleSliderCommit = (
    _: React.SyntheticEvent | Event,
    newValue: number | number[]
  ) => {
    commitDownPaymentChange(newValue as number);
  };

  const handleDownPaymentInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (readOnly) return;
    setDownPaymentInput(event.target.value);
  };

  const handleDownPaymentInputBlur = () => {
    if (downPaymentInput.trim() === "") {
      setDownPaymentInput(downPayment > 0 ? String(downPayment) : "");
      return;
    }

    const parsed = Number(downPaymentInput);
    if (Number.isNaN(parsed)) {
      setDownPaymentInput(downPayment > 0 ? String(downPayment) : "");
      return;
    }

    commitDownPaymentChange(parsed);
  };

  const handleDownPaymentInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  const handleDurationChange = (duration: number) => {
    if (readOnly) return;
    setSelectedDuration(duration);
    dispatch(setApplicationLoanTime(duration));
  };

  const handleDurationSelectChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleDurationChange(Number(event.target.value));
  };

  if ((preOffer || isCreditLine) && !offerResult) return <div />;

  if (!isCreditLine && !preOffer) {
    return (
      <>
        <div className="bg_secondary_100 border_solid border_1 border_secondary_500 br_xs p_sm m_t_sm display_flex flex_align_start flex_gap_sm">
          <Lock
            className="text_secondary_500 dim_30"
            style={{ stroke: "none", flexShrink: 0 }}
          />
          <Typography scale="small" weight="400" textColor="neutral_800">
            Continúa con el formulario de solicitud de crédito para conocer el{" "}
            <strong>plan detallado</strong> de esta moto: enganche mínimo, plazo y
            pago semanal.
          </Typography>
        </div>
        {onContinueBtn && <div>
          <Button
            className="w_100_per m_y_md"
            scale="small"
            onClick={onContinueBtn}
            disabled={btnDisabled}
          >
            <Typography scale="medium" weight="600" className="flex_center">
              Continuar con mi financimiento <ChevronRight />
            </Typography>
          </Button>
        </div>}
      </>
    );
  }

  return (
    <div className="p_lg border_solid border_1 border_neutral_300 br_xs">
      {showVehicleDetail && (
        <div className="m_b_md">
          <Typography scale="medium" weight="600" className="m_b_xs" component="p">
            Moto seleccionada
          </Typography>
          <div className="display_flex flex_wrap flex_align_start flex_justify_between flex_gap_md">
            <div className="display_flex flex_gap_md flex_align_center">
              {subjectImage && (
                <img
                  src={
                    subjectImage.formats?.small?.url ?? subjectImage.url
                  }
                  alt={subjectTitle}
                  style={{
                    width: 100,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              )}
              <div className="display_flex flex_col flex_gap_xxs">
                <Typography scale="medium" weight="600" component="p" className="m_none">
                  {subjectTitle}{" "}
                  {subjectCc}cc
                </Typography>
                <Typography scale="small" weight="400" component="p" className="m_none">
                  Año {subjectYear}
                </Typography>
                <Typography scale="small" weight="600" component="p" className="m_none">
                  {priceFormatFinancial(vehiclePrice, 0)} MXN
                </Typography>
              </div>
            </div>
            <ReachChip reaches={reaches} />
          </div>
        </div>
      )}

      <div className="m_b_md">
        <div
          className={
            !showVehicleDetail
              ? "display_flex flex_wrap flex_align_start flex_justify_between flex_gap_md"
              : ""
          }
        >
          <Typography scale="medium" weight="600" className="m_b_xs" component="p">
            Plazo de financiamiento
          </Typography>
          {!showVehicleDetail && <ReachChip reaches={reaches} />}
        </div>
        <Select
          variant="default"
          name="loanDuration"
          placeholder="Selecciona un plazo"
          value={selectedDuration || undefined}
          onChange={handleDurationSelectChange}
          options={allOffers.map((o) => ({
            value: o.duration,
            label: `${o.duration} semanas`,
          }))}
          disabled={loading || readOnly}
        />
      </div>

      {offerResult && selectedOffer && allOffers.length > 0 && (
        // eslint-disable-next-line no-nested-ternary
        <div className={`${loading ? "bg_neutral_100" : reaches ? "bg_green_100" : "bg_red_100"} br_xs p_md m_b_md`}>
          <Typography scale="medium" weight="600" className="m_b_xs m_t_none" component="p">
            Detalle del plan
          </Typography>
          <PlanRow
            label="Precio de la moto"
            value={`${priceFormatFinancial(selectedOffer.ticketPrice, 0)} MXN`}
            loading={loading}
          />
          {preOffer && (preOffer.totalInsuranceAmount ?? 0) > 0 && (
            <PlanRow
              sub
              label="Incluye seguro de cobertura amplia"
              value={`${priceFormatFinancial(preOffer.totalInsuranceAmount, 0)} MXN`}
              loading={loading}
            />
          )}
          {reaches && !loading && (
            <PlanRow
              label="Valor del enganche"
              value={`${priceFormatFinancial(selectedOffer.downPaymentAmount, 0)} MXN`}
              loading={loading}
            />
          )}

          {!reaches && downPaymentFeedback && !loading && (
            <>
              <PlanRow
                label={downPaymentFeedback.label}
                value={downPaymentFeedback.value}
                loading={loading}
                error
              />
              <PlanRow
                label="Valor de enganche requerido"
                value={`${priceFormatFinancial(selectedOffer.downPaymentAmount, 0)} MXN`}
                highlight
                loading={loading}
              />
            </>
          )}

          <PlanRow
            label="Monto a financiar"
            value={`${priceFormatFinancial(selectedOffer.financingAmount, 0)} MXN`}
            loading={loading || !reaches}
          />
          <div className="border_t_solid border_1 border_neutral_300 m_t_xs p_t_xs">
            <PlanRow
              label="Pago semanal"
              value={`${priceFormatFinancial(selectedOffer.periodicPayment, 0)} MXN`}
              loading={loading || !reaches}
              highlight
            />
          </div>
        </div>
      )}

      {offerResult && downPaymentBounds && downPaymentBounds.max === downPaymentBounds.min && downPaymentBounds.max > 0 && (
        <div className="m_b_md">
          <Typography scale="medium" weight="600" className="m_b_xs" component="p">
            Enganche requerido
          </Typography>
          <Typography scale="small" weight="400" textColor="neutral_800">
            {priceFormatFinancial(downPaymentBounds.min, 0)} MXN — este es el enganche fijo
            para esta moto según tu perfil.
          </Typography>
        </div>
      )}

      {!readOnly && offerResult && downPaymentBounds && downPaymentBounds.max > downPaymentBounds.min && (
        <div className="m_b_md">
          <Typography scale="medium" weight="600" className="m_b_xs" component="p">
            Ajusta tu enganche
          </Typography>
          {!reaches && downPayment < (offerResult.maxDownPaymentAmount ?? downPayment) && (
            <div className="bg_primary_25 border_solid border_1 border_primary_300 br_xs p_sm m_t_sm display_flex flex_align_start flex_gap_sm">
              <Warning
                className="text_primary_300 dim_30"
                style={{ stroke: "none", flexShrink: 0 }}
              />
              <Typography scale="small" weight="400" textColor="neutral_800">
                Necesitas un <strong>enganche superior</strong> para conocer el detalle de tu moto
              </Typography>
            </div>
          )}
          <div className="display_flex flex_wrap flex_gap_md p_x_md p_y_xs">
            <div className="flex_grow_3" style={{ minWidth: 200 }}>
              <Slider
                value={sliderValue}
                min={downPaymentBounds.min}
                max={downPaymentBounds.max}
                step={SLIDER_STEP}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderCommit}
                sx={{
                  color: "var(--primary_300, #35D17F)",
                  "& .MuiSlider-thumb": { borderRadius: "50%" },
                }}
              />
              <div className="display_flex flex_justify_between m_y_xxs">
                <Typography scale="xsmall" weight="400" textColor="neutral_600">
                  Mín. {priceFormatFinancial(downPaymentBounds.min, 0)} MXN
                </Typography>
                <Typography scale="xsmall" weight="400" textColor="neutral_600">
                  Máx. {priceFormatFinancial(downPaymentBounds.max, 0)} MXN
                </Typography>
              </div>
            </div>
            <div className="flex_grow_1" style={{ minWidth: 150 }}>
              <div className="display_flex flex_align_center flex_gap_xs w_100_per">
                <Typography scale="small" weight="400" textColor="neutral_600" component="p">
                  $
                </Typography>
                <div className="flex_grow_1">
                  <Input
                    type="number"
                    name="downPayment"
                    title=""
                    placeholder={`Entre ${priceFormatFinancial(downPaymentBounds.min, 0)} y ${priceFormatFinancial(downPaymentBounds.max, 0)} MXN`}
                    value={downPaymentInput}
                    onChange={handleDownPaymentInputChange}
                    onBlur={handleDownPaymentInputBlur}
                    onKeyDown={handleDownPaymentInputKeyDown}
                    min={downPaymentBounds.min}
                    max={downPaymentBounds.max}
                    inputClassName="text_center"
                    style={{ textAlign: "center" }}
                  />
                </div>
                <Typography scale="small" weight="400" textColor="neutral_600" component="p">
                  MXN
                </Typography>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <Typography scale="small" weight="400" textColor="neutral_600" component="p">
          Calculando tu plan...
        </Typography>
      )}

      {allOffers.length === 0 && offerResult?.maxDownPaymentAmount === downPayment && (
        <div className="bg_red_100 border_solid border_1 border_red_300 br_xs p_sm m_t_sm display_flex flex_align_start flex_gap_sm">
            <Lock
              className="text_red_300 dim_30"
              style={{ stroke: "none", flexShrink: 0 }}
            />
            <Typography scale="small" weight="400" textColor="neutral_800">
              Esta motocicleta no cumple con las condiciones aprobadas para tu perfil. Te invitamos a seguir explorando nuestro catálogo.
            </Typography>
          </div>
      )}

      {allOffers.length === 0 && (
        <Button
              className="w_100_per m_t_md"
              scale="small"
              variant="outline"
              onClick={() => navigate("/catalogo")}
            >
              <Typography scale="medium" weight="600" className="flex_center">
                Seguir explorando
              </Typography>
            </Button>
      )}

      {allOffers.length > 0 && onContinueBtn && <div>
        <Button
          className="w_100_per m_y_md"
          scale="small"
          onClick={onContinueBtn}
          disabled={btnDisabled}
        >
          <Typography scale="medium" weight="600" className="flex_center">
            Continuar con mi financimiento <ChevronRight />
          </Typography>
        </Button>
      </div>}
    </div>
  );
};

export default LoanCalculator;
